import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/apiClient";
import { ReviewCard } from "@/components/QuestionView";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock, ShieldCheck } from "lucide-react";

export default function SimulationResult() {
  const { attemptId } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fired = useRef(false);

  const load = async () => {
    try { const { data } = await api.get(`/simulation/${attemptId}/results`); setData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const sid = params.get("session_id");
    const run = async () => {
      if (sid) {
        for (let i = 0; i < 4; i++) {
          try {
            const { data } = await api.get(`/payments/status/${sid}`);
            if (data.payment_status === "paid") {
              if (!fired.current && window.gtag) {
                fired.current = true;
                window.gtag("event", "conversion", {
                  send_to: "AW-18434067436/fEfQCNmn3u8cEOyXhtZE",
                  value: (data.amount || 0) / 100 || 1.0,
                  currency: (data.currency || "eur").toUpperCase(),
                  transaction_id: sid,
                });
              }
              break;
            }
          } catch { /* ignore */ }
          await new Promise((r) => setTimeout(r, 1500));
        }
      }
      await load();
    };
    run();
    // eslint-disable-next-line
  }, [attemptId]);

  const unlock = async () => {
    try {
      const { data } = await api.post(`/simulation/${attemptId}/checkout`, { lookup_key: "sim_unlock", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <Layout><div className="max-w-3xl mx-auto p-10 text-slate-500">Caricamento…</div></Layout>;
  if (!data) return <Layout><div className="max-w-3xl mx-auto p-10">Simulazione non trovata.</div></Layout>;

  if (data.locked) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 border-2 border-[#002FA7] mb-6"><Lock className="w-8 h-8 text-[#002FA7]" /></div>
          <div className="label-mono text-[#002FA7] mb-3">— Simulazione completata</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mb-4">I tuoi risultati sono pronti</h1>
          <p className="text-slate-600 mb-2">Hai completato le tre sezioni cronometrate.</p>
          <p className="text-slate-600 mb-8">Sblocca il <span className="font-semibold">punteggio dettagliato per sezione</span> e la <span className="font-semibold">spiegazione di ogni risposta</span>.</p>
          <div className="border border-[#002FA7] bg-[#0A0F1A] text-white p-8 premium-grain mb-6">
            <div className="font-display font-black text-5xl mb-1">9,99€</div>
            <div className="label-mono text-slate-300 mb-6">accesso completo ai risultati di questa simulazione</div>
            <Button data-testid="sim-unlock-btn" onClick={unlock} className="w-full rounded-none bg-[#FFD700] hover:bg-white text-[#0A0F1A] label-mono py-6">
              Sblocca i risultati
            </Button>
            <div className="flex items-center justify-center gap-2 mt-4 text-xs text-slate-400 label-mono"><ShieldCheck className="w-4 h-4" />Pagamento sicuro con Stripe</div>
          </div>
          <Link to="/simulazione" className="text-sm text-slate-500 underline">← Nuova simulazione</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
        <div className="border border-slate-200 bg-white p-10 mb-8 shadow-hard-black" data-testid="sim-result-score">
          <div className="label-mono text-[#002FA7] mb-2">— Risultato simulazione</div>
          <h1 className="font-display font-black text-6xl tracking-tighter">{data.score_percent}%</h1>
          <p className="text-slate-600 mt-4">{data.correct} corrette su {data.total}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          {data.section_scores.map((s) => (
            <div key={s.category} className="border border-slate-200 bg-white p-6">
              <div className="label-mono text-slate-500 mb-1">{s.title}</div>
              <div className="font-display font-black text-3xl">{s.score_percent}%</div>
              <div className="text-sm text-slate-500 font-mono-num">{s.correct}/{s.total}</div>
            </div>
          ))}
        </div>
        <h2 className="font-display font-bold text-2xl mb-6">Revisione completa</h2>
        <div className="space-y-4">{data.details.map((d, i) => <ReviewCard key={d.question_id} d={d} index={i} />)}</div>
        <div className="mt-8"><Link to="/simulazione"><Button className="rounded-none bg-[#002FA7] hover:bg-[#001d6b] label-mono py-6">Nuova simulazione</Button></Link></div>
      </div>
    </Layout>
  );
}
